import React, { useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Checkbox, Stack, Typography, IconButton, Menu, MenuItem, TablePagination, LinearProgress,
  Tooltip, useMediaQuery, useTheme, Card, ListItemIcon, ListItemText, ListSubheader, alpha,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import {
  Pagination, SalonMember, TaskListItem, TaskStatus,
} from '../../services/taskService';
import { tokens } from '../../theme';
import { EmptyState, LoadingState, PersonAvatar, SectionCard } from '../../components/ui';
import {
  AssigneeAvatars, CategoryTag, DueIndicator, MetaItem, PriorityChip, StatusChip, STATUS_COLOR, STATUS_LABEL,
  STATUS_ORDER, formatDate, formatDuration, userLabel,
} from './taskShared';

interface Props {
  tasks: TaskListItem[];
  pagination: Pagination;
  loading: boolean;
  selected: string[];
  members: SalonMember[];
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  onSelect: (ids: string[]) => void;
  onOpenTask: (id: string) => void;
  onQuickStatus: (task: TaskListItem, status: TaskStatus) => void;
  onQuickAssign: (task: TaskListItem, userId: string) => void;
  onCardMenu: (event: React.MouseEvent<HTMLElement>, task: TaskListItem) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  canManage: boolean;
  /** Pusty stan listy (np. z przyciskiem „Nowe zadanie”); domyslnie ogolny komunikat. */
  emptyState?: React.ReactNode;
}

const COLUMNS: { id: string; label: string; sortable: boolean; hideOnTablet?: boolean; wideOnly?: boolean; align?: 'right' }[] = [
  { id: 'title', label: 'Zadanie', sortable: true },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'priority', label: 'Priorytet', sortable: true },
  { id: 'assignee', label: 'Przypisane', sortable: true },
  { id: 'dueAt', label: 'Termin', sortable: true },
  { id: 'trackedSeconds', label: 'Czas', sortable: true, hideOnTablet: true, align: 'right' },
  { id: 'createdAt', label: 'Utworzono', sortable: true, hideOnTablet: true, wideOnly: true },
];

const menuHeaderSx = { fontSize: '0.75rem', fontWeight: 700, color: tokens.muted, lineHeight: '32px', bgcolor: 'transparent', px: 1.5 } as const;

export const TaskListView: React.FC<Props> = ({
  tasks, pagination, loading, selected, members, sortBy, sortDir, onSort, onSelect, onOpenTask,
  onQuickStatus, onQuickAssign, onCardMenu, onPageChange, onLimitChange, canManage, emptyState,
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('lg'));
  // Data utworzenia miesci sie dopiero na szerokim ekranie — ponizej tabela przewijalaby sie w bok.
  const isWide = useMediaQuery(muiTheme.breakpoints.up('xl'));

  const [statusMenu, setStatusMenu] = useState<{ el: HTMLElement; task: TaskListItem } | null>(null);
  const [assignMenu, setAssignMenu] = useState<{ el: HTMLElement; task: TaskListItem } | null>(null);

  const allSelected = tasks.length > 0 && tasks.every((t) => selected.includes(t.id));
  const someSelected = selected.length > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) onSelect(selected.filter((id) => !tasks.some((t) => t.id === id)));
    else onSelect(Array.from(new Set([...selected, ...tasks.map((t) => t.id)])));
  };

  const toggleOne = (id: string) => {
    onSelect(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };

  const quickStatusMenu = statusMenu && (
    <Menu anchorEl={statusMenu.el} open onClose={() => setStatusMenu(null)}>
      <ListSubheader disableSticky sx={menuHeaderSx}>Zmień status</ListSubheader>
      {STATUS_ORDER.filter((s) => s !== statusMenu.task.status).map((status) => (
        <MenuItem
          key={status}
          onClick={() => { onQuickStatus(statusMenu.task, status); setStatusMenu(null); }}
        >
          <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLOR[status].dot, mr: 1.25, flexShrink: 0 }} />
          <ListItemText>{STATUS_LABEL[status]}</ListItemText>
        </MenuItem>
      ))}
    </Menu>
  );

  const quickAssignMenu = assignMenu && (
    <Menu anchorEl={assignMenu.el} open onClose={() => setAssignMenu(null)}>
      <ListSubheader disableSticky sx={menuHeaderSx}>Przypisz osobę</ListSubheader>
      {members.length === 0 && (
        <MenuItem disabled>
          <ListItemText>Brak pracowników</ListItemText>
        </MenuItem>
      )}
      {members.map((m) => {
        const already = assignMenu.task.assignees.some((a) => a.id === m.userId);
        return (
          <MenuItem
            key={m.userId}
            onClick={() => { onQuickAssign(assignMenu.task, m.userId); setAssignMenu(null); }}
          >
            <ListItemIcon sx={{ minWidth: 34 }}>
              <Checkbox edge="start" size="small" checked={already} tabIndex={-1} disableRipple />
            </ListItemIcon>
            <PersonAvatar firstName={m.firstName} lastName={m.lastName} fallback={m.email} seed={m.userId} size={26} sx={{ mr: 1.25, fontSize: '0.75rem' }} />
            <ListItemText>{userLabel({ id: m.userId, ...m })}</ListItemText>
          </MenuItem>
        );
      })}
    </Menu>
  );

  const pager = pagination.total > 0 && (
    <TablePagination
      component="div"
      count={pagination.total}
      page={Math.max(0, pagination.page - 1)}
      rowsPerPage={pagination.limit}
      onPageChange={(_, p) => onPageChange(p + 1)}
      onRowsPerPageChange={(e) => onLimitChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={[10, 25, 50, 100]}
      labelRowsPerPage={isMobile ? 'Na stronie:' : 'Zadań na stronie:'}
      labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
      sx={{ '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', px: { xs: 1.5, md: 2 } } }}
    />
  );

  if (loading && tasks.length === 0) {
    return <LoadingState variant="table" rows={6} />;
  }

  if (!loading && tasks.length === 0 && pagination.total === 0) {
    return (
      <>
        {emptyState || (
          <SectionCard>
            <EmptyState
              icon={<TaskAltOutlinedIcon />}
              title="Brak zadań"
              description="Zmień filtry lub utwórz nowe zadanie."
            />
          </SectionCard>
        )}
      </>
    );
  }

  // ── Mobile: cards instead of a horizontally scrolling table ────────────────
  if (isMobile) {
    return (
      <Box>
        {loading && <LinearProgress sx={{ mb: 1 }} />}
        <Stack gap={1}>
          {tasks.map((task) => {
            const isSelected = selected.includes(task.id);
            return (
              <Card
                key={task.id}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  borderColor: isSelected ? tokens.plumLight : task.isOverdue ? alpha(tokens.danger, 0.45) : tokens.line,
                  bgcolor: isSelected ? tokens.plumSofter : tokens.paper,
                  boxShadow: task.isOverdue ? `inset 3px 0 0 ${tokens.danger}` : 'none',
                }}
                onClick={() => onOpenTask(task.id)}
              >
                <Stack direction="row" gap={0.5} alignItems="flex-start">
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onClick={(e) => { e.stopPropagation(); toggleOne(task.id); }}
                    inputProps={{ 'aria-label': `Zaznacz ${task.title}` }}
                    sx={{ mt: -0.75, ml: -0.75 }}
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4, wordBreak: 'break-word' }}>{task.title}</Typography>
                    <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center" sx={{ mt: 0.75 }}>
                      <StatusChip status={task.status} />
                      <PriorityChip priority={task.priority} />
                      {task.category && <CategoryTag name={task.category.name} color={task.category.color} />}
                    </Stack>
                    <Stack direction="row" gap={1} alignItems="center" sx={{ mt: 1 }}>
                      <Stack direction="row" columnGap={1.25} rowGap={0.5} flexWrap="wrap" alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
                        <DueIndicator dueAt={task.dueAt} hasTime={task.dueHasTime} overdue={task.isOverdue} showEmpty />
                        {task.trackedSeconds > 0 && (
                          <MetaItem icon={<TimerOutlinedIcon />} label={formatDuration(task.trackedSeconds)} title="Zarejestrowany czas" />
                        )}
                      </Stack>
                      <AssigneeAvatars users={task.assignees} size={24} />
                      <IconButton
                        size="small"
                        aria-label="Akcje zadania"
                        onClick={(e) => { e.stopPropagation(); onCardMenu(e, task); }}
                        sx={{ mr: -0.75 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            );
          })}
        </Stack>
        {pager && <Paper sx={{ mt: 1.5, overflow: 'hidden', '& .MuiTablePagination-root': { borderTop: 0 } }}>{pager}</Paper>}
        {quickStatusMenu}
        {quickAssignMenu}
      </Box>
    );
  }

  // ── Desktop table ─────────────────────────────────────────────────────────
  const visibleColumns = COLUMNS.filter((c) => !(isTablet && c.hideOnTablet) && !(c.wideOnly && !isWide));

  return (
    <Paper sx={{ overflow: 'hidden', position: 'relative' }}>
      {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3, height: 3, borderRadius: 0 }} />}
      <TableContainer sx={{ maxHeight: 'calc(100vh - 330px)', minHeight: 200 }}>
        <Table stickyHeader sx={{ minWidth: isTablet ? 760 : 900 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                  inputProps={{ 'aria-label': 'Zaznacz wszystkie zadania' }}
                />
              </TableCell>
              {visibleColumns.map((col) => (
                <TableCell key={col.id} align={col.align} sortDirection={sortBy === col.id ? sortDir : false}>
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortBy === col.id}
                      direction={sortBy === col.id ? sortDir : 'asc'}
                      onClick={() => onSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ width: 56 }}><Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Akcje</Box></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => (
              <TableRow
                key={task.id}
                hover
                selected={selected.includes(task.id)}
                sx={{
                  cursor: 'pointer',
                  '&.Mui-selected, &.Mui-selected:hover': { bgcolor: tokens.plumSofter },
                }}
                onClick={() => onOpenTask(task.id)}
              >
                <TableCell padding="checkbox" sx={{ boxShadow: task.isOverdue ? `inset 3px 0 0 ${tokens.danger}` : 'none' }}>
                  <Checkbox
                    size="small"
                    checked={selected.includes(task.id)}
                    onClick={(e) => { e.stopPropagation(); toggleOne(task.id); }}
                    inputProps={{ 'aria-label': `Zaznacz ${task.title}` }}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 220, width: '40%' }}>
                  <Typography
                    title={task.title}
                    sx={{
                      fontSize: '0.9375rem', fontWeight: 600, color: tokens.ink, lineHeight: 1.4, wordBreak: 'break-word',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}
                  >
                    {task.title}
                  </Typography>
                  {(task.category || task.checklist.total > 0 || task.commentCount > 0 || task.attachmentCount > 0) && (
                    <Stack direction="row" columnGap={1.25} rowGap={0.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {task.category && <CategoryTag name={task.category.name} color={task.category.color} />}
                      {task.checklist.total > 0 && (
                        <MetaItem icon={<ChecklistIcon />} label={`${task.checklist.done}/${task.checklist.total}`} title="Checklista" />
                      )}
                      {task.commentCount > 0 && (
                        <MetaItem icon={<ChatBubbleOutlineIcon />} label={String(task.commentCount)} title="Komentarze" />
                      )}
                      {task.attachmentCount > 0 && (
                        <MetaItem icon={<AttachFileIcon />} label={String(task.attachmentCount)} title="Załączniki" />
                      )}
                    </Stack>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Zmień status">
                    <Box
                      component="button"
                      type="button"
                      aria-haspopup="menu"
                      aria-label={`Status: ${STATUS_LABEL[task.status]}. Zmień status`}
                      onClick={(e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); setStatusMenu({ el: e.currentTarget, task }); }}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.25, p: 0.25, m: -0.25, border: 0, bgcolor: 'transparent',
                        borderRadius: 999, cursor: 'pointer', font: 'inherit', color: tokens.muted,
                        '& .expand': { opacity: 0, transition: 'opacity .15s ease' },
                        '&:hover .expand, &:focus-visible .expand': { opacity: 1 },
                        '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 1 },
                      }}
                    >
                      <StatusChip status={task.status} size="medium" />
                      <ExpandMoreIcon className="expand" sx={{ fontSize: 18 }} />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell><PriorityChip priority={task.priority} /></TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <AssigneeAvatars users={task.assignees} size={28} />
                    {canManage && (
                      <Tooltip title="Szybkie przypisanie">
                        <IconButton
                          size="small"
                          aria-label="Przypisz pracownika"
                          onClick={(e) => { e.stopPropagation(); setAssignMenu({ el: e.currentTarget, task }); }}
                        >
                          <PersonAddAltIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <DueIndicator dueAt={task.dueAt} hasTime={task.dueHasTime} overdue={task.isOverdue} showEmpty />
                </TableCell>
                {!isTablet && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: task.trackedSeconds > 0 ? tokens.ink : tokens.subtle }}>
                    {task.trackedSeconds > 0 ? formatDuration(task.trackedSeconds) : '—'}
                  </TableCell>
                )}
                {isWide && (
                  <TableCell sx={{ whiteSpace: 'nowrap', color: tokens.muted, fontVariantNumeric: 'tabular-nums' }}>
                    {formatDate(task.createdAt)}
                  </TableCell>
                )}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label="Akcje zadania"
                    onClick={(e) => { e.stopPropagation(); onCardMenu(e, task); }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pager}
      {quickStatusMenu}
      {quickAssignMenu}
    </Paper>
  );
};

export default TaskListView;
